import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import CreditcardIcon from '@atlaskit/icon/glyph/creditcard';
import EditorTextStyleIcon from '@atlaskit/icon/glyph/editor/text-style';
import EmailIcon from '@atlaskit/icon/glyph/email';
import HomeIcon from '@atlaskit/icon/glyph/home';
import MediaServicesBrushIcon from '@atlaskit/icon/glyph/media-services/brush';
import ScreenIcon from '@atlaskit/icon/glyph/screen';
import WorldIcon from '@atlaskit/icon/glyph/world';

import LinkedInProfileIcon from './components/icons/LinkedInProfile';
import UsCurrencyIcon from './components/icons/UsCurrency';
import UsPhoneNumberIcon from './components/icons/UsPhoneNumber';

const CREDIT_CARD_REGEX = '^(5[1-5]\\d{2}|2[2-7]\\d{2})[ ]?\\d{4}[ ]?\\d{4}[ ]?\\d{4}$';
const DATE_FORMAT_REGEX = '^(0[1-9]|1[0-2])\\/(0[1-9]|[12][0-9]|3[01])\\/\\d{4}$';
const EMAIL_REGEX = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
const HEX_COLOR_REGEX = '^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$';
const IBAN_REGEX =
    '^([A-Z]{2}[ ' +
    '\\\\' +
    '-]?[0-9]{2})(?=(?:[ ' +
    '\\\\' +
    '-]?[A-Z0-9]){9,30}$)((?:[ ' +
    '\\\\' +
    '-]?[A-Z0-9]{3,5}){2,7})([ ' +
    '\\\\' +
    '-]?[A-Z0-9]{1,3})?$';
const IP_V6_REGEX =
    '^(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,7}:$|^(?:[a-fA-F0-9]{1,4}:){1,6}:[a-fA-F0-9]{1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,5}(?::[a-fA-F0-9]{1,4}){1,2}$|^(?:[a-fA-F0-9]{1,4}:){1,4}(?::[a-fA-F0-9]{1,4}){1,3}$|^(?:[a-fA-F0-9]{1,4}:){1,3}(?::[a-fA-F0-9]{1,4}){1,4}$|^(?:[a-fA-F0-9]{1,4}:){1,2}(?::[a-fA-F0-9]{1,4}){1,5}$|^(?:[a-fA-F0-9]{1,4}:):(?::[a-fA-F0-9]{1,4}){1,6}$|^:(?::[a-fA-F0-9]{1,4}){1,7}$|^(?:[a-fA-F0-9]{1,4}:){1,4}:(?:[a-fA-F0-9]{1,4}|((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))$';
const LINKED_IN_REGEX = '^linkedin\\.com\\/in\\/[a-zA-Z0-9-]{3,100}\\/?$';
const MAC_ADDRESS_REGEX = '^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$';
const NAME_SURNAME_REGEX = '^[A-Z][a-z]+(?: [A-Z][a-z]+)+$';
const SWIFT_CODE_REGEX = '^[A-Z]{4}[-]{0,1}[A-Z]{2}[-]{0,1}[A-Z0-9]{2}[-]{0,1}[0-9]{3}$';
const US_PHONE_NUMBER_REGEX = '\\(?\\d{3}\\)?-? *\\d{3}-? *-?\\d{4}';
const US_POSTAL_CODE_REGEX = '^\\d{5}(?:[-\\s]\\d{4})?$';
const US_CURRENCY_REGEX = '^\\$(0|[1-9][0-9]{0,2})(,\\d{3})*(\\.\\d{1,2})?$';

export const regexExamples = [
    {
        regex: CREDIT_CARD_REGEX,
        description: 'Example: 2222 4053 4324 8877',
        elemBefore: <CreditcardIcon label='credit card' />,
        label: 'Credit card validator (Mastercard)',
    },
    {
        regex: DATE_FORMAT_REGEX,
        description: 'Example: 12/31/2024',
        elemBefore: <CalendarIcon label='date' />,
        label: 'Date format',
    },
    {
        regex: EMAIL_REGEX,
        description: 'Example: contact@appsvio.com',
        elemBefore: <EmailIcon label='e-mail' />,
        label: 'Email address',
    },
    {
        regex: HEX_COLOR_REGEX,
        description: 'Example: #F5007E',
        elemBefore: <MediaServicesBrushIcon label='hex' />,
        label: 'HEX color',
    },
    {
        regex: IBAN_REGEX,
        description: 'Example: DE89 3704 0044 0532 0130 00',
        elemBefore: <CreditcardIcon label='iban' />,
        label: 'IBAN',
    },
    {
        regex: IP_V6_REGEX,
        description: 'Example: 2001:0000:130F:0000:0000:09C0:876A:130B',
        elemBefore: <ScreenIcon label='IPv6' />,
        label: 'IPv6',
    },
    {
        regex: LINKED_IN_REGEX,
        description: 'Example: linkedin.com/in/john-doe/',
        elemBefore: <LinkedInProfileIcon label='LinkedIn' />,
        label: 'LinkedIn profile',
    },
    {
        regex: MAC_ADDRESS_REGEX,
        description: 'Example: 00:B0:D0:63:C2:26',
        elemBefore: <WorldIcon label='MAC address' />,
        label: 'MAC address',
    },
    {
        regex: NAME_SURNAME_REGEX,
        description: 'Example: John Doe',
        elemBefore: <EditorTextStyleIcon label='Name and surname' />,
        label: 'Name and surname',
    },
    {
        regex: SWIFT_CODE_REGEX,
        description: 'Example: AAAA-BB-CC-123',
        elemBefore: <CreditcardIcon label='SWIFT code' />,
        label: 'SWIFT code',
    },
    {
        regex: US_PHONE_NUMBER_REGEX,
        description: 'Example: +1-212-456-7890',
        elemBefore: <UsPhoneNumberIcon label='US Phone number' />,
        label: 'US Phone number',
    },
    {
        regex: US_POSTAL_CODE_REGEX,
        description: 'Example: 33138',
        elemBefore: <HomeIcon label='US Postal code' />,
        label: 'US Postal code',
    },
    {
        regex: US_CURRENCY_REGEX,
        description: 'Example: $350.75',
        elemBefore: <UsCurrencyIcon label='USD currency' />,
        label: 'USD currency',
    },
];
